import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { HiveAccent } from '@/components/brand/hive-accent';
import { api } from '@/lib/api';
import { fmtZoned } from '@/lib/utils';

/**
 * Tracking loss, on every screen, until it is fixed.
 *
 * GirOrganic's live theme dropped the Cookiebees tag on 26 Sep 2026 and the
 * only place that said so was an alerts page nobody had open and one email to
 * one address. Every report the owner read in between was quietly missing its
 * site activity. So an open tracking-loss incident (services/trackingLoss:
 * page views stopped, or the live theme does not load the tag) sits above
 * every page as a band that names the theme and links the fix.
 *
 * Deliberately NOT dismissable: it clears itself when the tag is back and the
 * page views have held above half of normal for two checks in a row. A band
 * that can be waved away is one that gets waved away on the first day.
 */
export function TrackingLossBanner() {
  const { data } = useQuery({
    queryKey: ['health-watch', 'tracking'],
    queryFn: async () => (await api.get('/api/health-watch/tracking')).data,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  const alert = (data?.alerts || [])[0];
  if (!alert) return null;

  const when = alert.stopped_words
    ? `It stopped ${alert.stopped_words}.`
    : `Since ${fmtZoned(alert.opened_at, 'd MMM, hh:mm aaa')}.`;
  const kept = alert.kept_by_pixel ? ' The Shopify pixel is keeping page views meanwhile.' : '';
  const action = alert.action || { label: 'Get the site script', href: '/settings?tab=tracking&sub=install' };

  return (
    <div role="alert" className="flex items-center gap-3 border-b card-critical px-4 py-1.5 text-[13px] text-red-800 dark:text-red-200">
      <HiveAccent className="-my-2 hidden w-16 sm:block" />
      <span className="min-w-0 flex-1">
        <span className="font-semibold">{alert.title}.</span>{' '}
        {when}{kept}{' '}
        {alert.recovering && <span>Page views are coming back; this clears once they hold. </span>}
        <Link to={action.href} className="font-medium underline underline-offset-2">{action.label}</Link>
        <span aria-hidden> · </span>
        <Link to="/tracking/health" className="underline underline-offset-2 opacity-80 hover:opacity-100">What we saw</Link>
      </span>
    </div>
  );
}

export default TrackingLossBanner;
